import {
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsList,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Form } from 'reactstrap';

import { PathsLayouts } from '../../../../config/routes';
import { TranslationsKeys } from '../../../../locales/translations';
import { useCourseTabPermissions } from '../hooks/useCourseTabPermissions';
import { CourseFormContext } from '../providers/CourseProvider';
import { CourseFormFields, CourseFormMode } from '../types';
import { courseToFormFields, submitRequiresConfirmation } from '../utils';
import CourseFooterSection from './sections/CourseFooterSection';
import CourseHeaderSection from './sections/CourseHeaderSection';
import CourseAdministrativeTab from './tabs/CourseAdministrativeTab';
import CourseTeachingTab from './tabs/CourseTeachingTab';

type CourseFormProps = {
  processing?: boolean;
  onSubmitAdmin: (p: {
    values: CourseFormFields;
    isDisabledToUpdate: boolean;
  }) => void;
  onSubmitTeaching: (p: { values: CourseFormFields }) => void;
  onBack?: () => void;
};

const CourseForm = ({
  processing,
  onBack,
  onSubmitAdmin,
  onSubmitTeaching,
}: CourseFormProps) => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);
  const { mode, course, formData, isDisabledToUpdate } = useContext(
    CourseFormContext,
  );
  const perms = useCourseTabPermissions();
  const location = useLocation();
  const history = useHistory();

  const isCreate = mode === CourseFormMode.CREATE;
  const showAdminTab = perms.canReadAdministrative;
  // Bug 1.a: en CREATE no existe aún el Course en BD, así que la pestaña
  // Docencia (que depende de unidades / resultados de aprendizaje) se oculta.
  const showTeachingTab = !isCreate && perms.canReadTeaching;

  // Dos instancias independientes de useForm, una por pestaña. Cada una se
  // encarga de su propio `handleSubmit`. Así, hacer click en un Tab (que es
  // un <button> del global-ui) nunca puede disparar un submit, porque los
  // Tabs viven FUERA de cualquier <form>.
  const methodsAdmin = useForm<CourseFormFields>({
    defaultValues: {
      isCreditsNotApplicable: false,
    },
  });
  const methodsTeaching = useForm<CourseFormFields>();

  const handleCancel = useCallback(() => {
    history.push(`${PathsLayouts.academicOffers}/curricular-subjects`);
  }, [history]);

  const initialTab = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const requested = sp.get('tab');
    if (requested === 'teaching' && showTeachingTab) {
      return 'teaching';
    }
    if (showAdminTab) {
      return 'administrative';
    }
    if (showTeachingTab) {
      return 'teaching';
    }
    return 'administrative';
  }, [location.search, showAdminTab, showTeachingTab]);

  const saveAdmin = useCallback(
    async (values: CourseFormFields) => {
      if (!values?.attendance?.length) {
        addToast({
          icon: 'warning',
          color: 'warning',
          text: t(`validateAttendance`),
        });
        return;
      }

      if (submitRequiresConfirmation(values, mode)) {
        return showDialogConfirm({
          icon: {
            name: 'warning',
            color: 'warning',
          },
          title: t(`confirmModalTitle`),
          subtitle: t(`confirmModalBody`),
          btnConfirm: {
            text: t(`confirmModalAgree`),
            onConfirm: () => {
              onSubmitAdmin({
                values,
                isDisabledToUpdate,
              });
            },
          },
          btnClose: {
            text: t(`confirmModalCancel`),
          },
        });
      } else {
        onSubmitAdmin({
          values,
          isDisabledToUpdate,
        });
      }
    },
    [t, onSubmitAdmin, isDisabledToUpdate, mode],
  );

  const saveTeaching = useCallback(
    async (values: CourseFormFields) => {
      onSubmitTeaching({ values });
    },
    [onSubmitTeaching],
  );

  useEffect(() => {
    if (course && formData) {
      const values = courseToFormFields(course, formData);
      methodsAdmin.reset(values);
      methodsTeaching.reset(values);
    }
    // Reset solo depende de los datos de origen; incluir los objetos `methods*`
    // generaría un bucle ya que reset cambia el estado interno de RHF.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, formData]);

  // CREATE mode: seed courseTypes with the first available type (Obligatorio)
  // since the CourseTypeSection UI was removed.
  useEffect(() => {
    if (!course && formData?.courseTypes?.length) {
      methodsAdmin.setValue('courseTypes', [+formData.courseTypes[0].value]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, formData?.courseTypes]);

  const tabs = [
    showAdminTab && (
      <Tab key="administrative" id="administrative">
        {t('tabs.administrative')}
      </Tab>
    ),
    showTeachingTab && (
      <Tab key="teaching" id="teaching">
        {t('tabs.teaching')}
      </Tab>
    ),
  ].filter(Boolean) as JSX.Element[];

  const panels = [
    showAdminTab && (
      <TabPanel key="administrative" id="administrative">
        <Form onSubmit={methodsAdmin.handleSubmit(saveAdmin)}>
          <FormProvider {...methodsAdmin}>
            <CourseAdministrativeTab readOnly={!perms.canWriteAdministrative} />
            <CourseFooterSection
              processing={processing}
              onCancel={handleCancel}
              showSave={perms.canWriteAdministrative}
            />
          </FormProvider>
        </Form>
      </TabPanel>
    ),
    showTeachingTab && (
      <TabPanel key="teaching" id="teaching">
        <Form onSubmit={methodsTeaching.handleSubmit(saveTeaching)}>
          <FormProvider {...methodsTeaching}>
            <CourseTeachingTab readOnly={!perms.canWriteTeaching} />
            <CourseFooterSection
              processing={processing}
              onCancel={handleCancel}
              showSave={perms.canWriteTeaching}
            />
          </FormProvider>
        </Form>
      </TabPanel>
    ),
  ].filter(Boolean) as JSX.Element[];

  return (
    <>
      <CourseHeaderSection onBack={onBack} />
      <div className="mt-4">
        <Tabs defaultTab={initialTab}>
          <TabsList>{tabs}</TabsList>
          <TabPanels>{panels}</TabPanels>
        </Tabs>
      </div>
    </>
  );
};

export default CourseForm;
